import { useState, useEffect, useCallback, useRef } from "react";
import { StoryboardCanvas } from "./StoryboardCanvas";
import { StoryboardToolbar } from "./StoryboardToolbar";

type StoryboardImage = {
  id: string;
  imageUrl: string;
  caption: string | null;
  x: number | null;
  y: number | null;
  width: number | null;
  height: number | null;
  zIndex: number | null;
};

type StoryboardNote = {
  id: string;
  content: string;
  x: number;
  y: number;
  width: number | null;
  zIndex: number | null;
  color: string | null;
};

type Props = {
  sceneId: string;
  sceneName: string;
  isOpen: boolean;
  onClose: () => void;
  canEdit: boolean;
};

type HistoryState = {
  images: StoryboardImage[];
  notes: StoryboardNote[];
};

export function StoryboardModal({ sceneId, sceneName, isOpen, onClose, canEdit }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<StoryboardImage[]>([]);
  const [notes, setNotes] = useState<StoryboardNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  // Undo/Redo history
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoingRef = useRef(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Lightbox state
  const [lightboxImageId, setLightboxImageId] = useState<string | null>(null);

  // Help overlay state
  const [showHelp, setShowHelp] = useState(false);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Save current state to history
  const saveToHistory = useCallback((newImages: StoryboardImage[], newNotes: StoryboardNote[]) => {
    if (isUndoingRef.current) return;

    setHistory(prev => {
      // Remove any future history if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      // Add new state
      newHistory.push({ images: newImages, notes: newNotes });
      // Keep only last 50 states
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  // Undo
  const handleUndo = useCallback(() => {
    if (!canUndo) return;
    isUndoingRef.current = true;
    const prevState = history[historyIndex - 1];
    setImages(prevState.images);
    setNotes(prevState.notes);
    setHistoryIndex(prev => prev - 1);
    setTimeout(() => { isUndoingRef.current = false; }, 0);
  }, [canUndo, history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (!canRedo) return;
    isUndoingRef.current = true;
    const nextState = history[historyIndex + 1];
    setImages(nextState.images);
    setNotes(nextState.notes);
    setHistoryIndex(prev => prev + 1);
    setTimeout(() => { isUndoingRef.current = false; }, 0);
  }, [canRedo, history, historyIndex]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    if (!isOpen || !canEdit) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, canEdit, handleUndo, handleRedo]);

  // Load scene data
  useEffect(() => {
    if (!isOpen) return;

    async function loadData() {
      setIsLoading(true);
      try {
        const res = await fetch(`/api/scenes/${sceneId}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          // Auto-arrange images that have no position set
          const arrangedImages = arrangeImagesIfNeeded(data.scene.images || []);
          const loadedNotes = data.scene.notes || [];
          setImages(arrangedImages);
          setNotes(loadedNotes);
          // Initialize history with loaded state
          setHistory([{ images: arrangedImages, notes: loadedNotes }]);
          setHistoryIndex(0);
        }
      } catch (e) {
        console.error("Failed to load storyboard data:", e);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [sceneId, isOpen]);

  // Auto-arrange images in grid if they have no position
  const arrangeImagesIfNeeded = (imgs: StoryboardImage[]): StoryboardImage[] => {
    return imgs.map((img, i) => {
      if (img.x === 0 && img.y === 0 && img.width === 200 && img.height === 150) {
        // Default values - arrange in grid
        return {
          ...img,
          x: (i % 4) * 230 + 50,
          y: Math.floor(i / 4) * 200 + 50,
        };
      }
      return img;
    });
  };

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;

    // Push a new history state when modal opens
    const stateKey = `storyboard-${sceneId}`;
    window.history.pushState({ storyboard: stateKey }, "");

    const handlePopState = (e: PopStateEvent) => {
      // If user pressed back, close the modal
      if (!e.state?.storyboard || e.state.storyboard !== stateKey) {
        onClose();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      // Clean up history state if modal closes normally (not via back button)
      if (window.history.state?.storyboard === stateKey) {
        window.history.back();
      }
    };
  }, [isOpen, sceneId, onClose]);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(3, z * 1.2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(0.1, z / 1.2));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
  }, []);

  const handleAddNote = useCallback(async (x: number, y: number) => {
    if (!canEdit) return;

    try {
      const res = await fetch(`/api/scenes/${sceneId}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          content: "",
          x: Math.round(x),
          y: Math.round(y),
          width: 200,
          zIndex: Math.max(0, ...images.map((i) => i.zIndex ?? 0), ...notes.map((n) => n.zIndex ?? 0)) + 1,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        const newNotes = [...notes, data.note];
        setNotes(newNotes);
        saveToHistory(images, newNotes);
      }
    } catch (e) {
      console.error("Failed to create note:", e);
    }
  }, [canEdit, sceneId, images, notes, saveToHistory]);

  const handleUpdateImage = useCallback(async (id: string, updates: Partial<StoryboardImage>) => {
    if (!canEdit) return;

    // Optimistic update
    const newImages = images.map((img) => (img.id === id ? { ...img, ...updates } : img));
    setImages(newImages);
    saveToHistory(newImages, notes);

    try {
      await fetch(`/api/scenes/${sceneId}/images/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
    } catch (e) {
      console.error("Failed to update image:", e);
    }
  }, [canEdit, sceneId, images, notes, saveToHistory]);

  const handleUpdateNote = useCallback(async (id: string, updates: Partial<StoryboardNote>) => {
    if (!canEdit) return;

    // Optimistic update
    const newNotes = notes.map((note) => (note.id === id ? { ...note, ...updates } : note));
    setNotes(newNotes);
    saveToHistory(images, newNotes);

    try {
      await fetch(`/api/scenes/${sceneId}/notes/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
    } catch (e) {
      console.error("Failed to update note:", e);
    }
  }, [canEdit, sceneId, images, notes, saveToHistory]);

  const handleDeleteImage = useCallback(async (id: string) => {
    if (!canEdit) return;

    const newImages = images.filter((img) => img.id !== id);
    setImages(newImages);
    saveToHistory(newImages, notes);

    try {
      await fetch(`/api/scenes/${sceneId}/images/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch (e) {
      console.error("Failed to delete image:", e);
    }
  }, [canEdit, sceneId, images, notes, saveToHistory]);

  const handleDeleteNote = useCallback(async (id: string) => {
    if (!canEdit) return;

    const newNotes = notes.filter((note) => note.id !== id);
    setNotes(newNotes);
    saveToHistory(images, newNotes);

    try {
      await fetch(`/api/scenes/${sceneId}/notes/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch (e) {
      console.error("Failed to delete note:", e);
    }
  }, [canEdit, sceneId, images, notes, saveToHistory]);

  const handleUploadImages = useCallback(async (files: FileList | File[], dropPosition?: { x: number; y: number }) => {
    if (!canEdit || files.length === 0) return;

    setIsUploading(true);
    const filesArray = Array.from(files);
    setUploadProgress({ current: 0, total: filesArray.length });

    try {
      for (let i = 0; i < filesArray.length; i++) {
        setUploadProgress({ current: i + 1, total: filesArray.length });
        const file = filesArray[i];
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(`/api/scenes/${sceneId}/images/upload`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        if (res.ok) {
          const data = await res.json();

          // Calculate position: use drop position if provided, otherwise grid
          let newX: number;
          let newY: number;

          if (dropPosition) {
            // Place at drop position, offset each subsequent image
            newX = Math.round(dropPosition.x + i * 220);
            newY = Math.round(dropPosition.y);
          } else {
            // Fallback to grid positioning
            const lastImage = images[images.length - 1];
            newX = lastImage ? (lastImage.x ?? 0) + 230 : 50;
            newY = lastImage ? lastImage.y ?? 50 : 50;
            if (newX > 1000) {
              newX = 50;
              newY = (newY ?? 50) + 200;
            }
          }

          const newImage: StoryboardImage = {
            ...data.image,
            x: newX,
            y: newY,
          };

          setImages((prev) => [...prev, newImage]);

          // Update position on server
          await fetch(`/api/scenes/${sceneId}/images/${data.image.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ x: newImage.x, y: newImage.y }),
          });
        }
      }
    } catch (e) {
      console.error("Failed to upload images:", e);
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  }, [canEdit, sceneId, images]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleBringToFront = useCallback((type: "image" | "note", id: string) => {
    const maxZ = Math.max(0, ...images.map((i) => i.zIndex ?? 0), ...notes.map((n) => n.zIndex ?? 0));
    const newZ = maxZ + 1;

    if (type === "image") {
      handleUpdateImage(id, { zIndex: newZ });
    } else {
      handleUpdateNote(id, { zIndex: newZ });
    }
  }, [images, notes, handleUpdateImage, handleUpdateNote]);

  // Handle selection change from canvas
  const handleSelectionChange = useCallback((ids: Set<string>) => {
    setSelectedIds(ids);
  }, []);

  // Delete all selected items
  const handleDeleteSelected = useCallback(async () => {
    if (!canEdit || selectedIds.size === 0) return;

    const count = selectedIds.size;
    if (!confirm(`Delete ${count} selected item${count > 1 ? 's' : ''}?`)) return;

    // Separate images and notes
    const selectedImageIds = images.filter(img => selectedIds.has(img.id)).map(img => img.id);
    const selectedNoteIds = notes.filter(note => selectedIds.has(note.id)).map(note => note.id);

    // Delete images
    for (const id of selectedImageIds) {
      await handleDeleteImage(id);
    }

    // Delete notes
    for (const id of selectedNoteIds) {
      await handleDeleteNote(id);
    }

    setSelectedIds(new Set());
  }, [canEdit, selectedIds, images, notes, handleDeleteImage, handleDeleteNote]);

  // Handle image double-click for lightbox
  const handleImageDoubleClick = useCallback((imageId: string) => {
    setLightboxImageId(imageId);
  }, []);

  // Lightbox navigation
  const lightboxImage = lightboxImageId ? images.find(img => img.id === lightboxImageId) : null;
  const lightboxIndex = lightboxImageId ? images.findIndex(img => img.id === lightboxImageId) : -1;

  const handleLightboxPrev = useCallback(() => {
    if (lightboxIndex > 0) {
      setLightboxImageId(images[lightboxIndex - 1].id);
    }
  }, [lightboxIndex, images]);

  const handleLightboxNext = useCallback(() => {
    if (lightboxIndex < images.length - 1) {
      setLightboxImageId(images[lightboxIndex + 1].id);
    }
  }, [lightboxIndex, images]);

  const handleLightboxClose = useCallback(() => {
    setLightboxImageId(null);
  }, []);

  // Lightbox keyboard navigation
  useEffect(() => {
    if (!lightboxImageId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleLightboxClose();
      } else if (e.key === 'ArrowLeft') {
        handleLightboxPrev();
      } else if (e.key === 'ArrowRight') {
        handleLightboxNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImageId, handleLightboxClose, handleLightboxPrev, handleLightboxNext]);

  // Help keyboard shortcut
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        // Don't trigger if user is typing in an input
        if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        setShowHelp(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      <StoryboardToolbar
        sceneName={sceneName}
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        onAddNote={() => handleAddNote(100, 100)}
        onUploadImages={handleUploadClick}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        onClose={onClose}
        canEdit={canEdit}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        selectedCount={selectedIds.size}
        onDeleteSelected={handleDeleteSelected}
        onShowHelp={() => setShowHelp(true)}
      />

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <StoryboardCanvas
          images={images}
          notes={notes}
          zoom={zoom}
          setZoom={setZoom}
          canEdit={canEdit}
          onAddNote={handleAddNote}
          onUpdateImage={handleUpdateImage}
          onUpdateNote={handleUpdateNote}
          onDeleteImage={handleDeleteImage}
          onDeleteNote={handleDeleteNote}
          onBringToFront={handleBringToFront}
          onUploadImages={handleUploadImages}
          onSelectionChange={handleSelectionChange}
          onDeleteSelected={handleDeleteSelected}
          onImageDoubleClick={handleImageDoubleClick}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        onChange={(e) => {
          if (e.target.files) {
            handleUploadImages(e.target.files);
            e.target.value = "";
          }
        }}
        className="hidden"
      />

      {/* Image Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={handleLightboxClose}
        >
          {/* Close button */}
          <button
            onClick={handleLightboxClose}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Previous button */}
          {lightboxIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleLightboxPrev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Image */}
          <img
            src={lightboxImage.imageUrl}
            alt={lightboxImage.caption || "Storyboard image"}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          {lightboxIndex < images.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleLightboxNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Caption and counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
            {lightboxImage.caption && (
              <p className="text-white/90 text-lg mb-2">{lightboxImage.caption}</p>
            )}
            <p className="text-white/50 text-sm">
              {lightboxIndex + 1} / {images.length}
            </p>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help Overlay */}
      {showHelp && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="bg-surface border border-border rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-lg font-medium text-foreground">Keyboard Shortcuts</h2>
              <button
                onClick={() => setShowHelp(false)}
                className="p-1 text-muted hover:text-foreground transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <ShortcutRow keys={["Ctrl", "Z"]} description="Undo" />
              <ShortcutRow keys={["Ctrl", "Shift", "Z"]} description="Redo" />
              <ShortcutRow keys={["Ctrl", "Y"]} description="Redo (alternate)" />
              <ShortcutRow keys={["Delete"]} description="Delete selected items" />
              <ShortcutRow keys={["Shift", "Drag"]} description="Box select multiple items" />
              <ShortcutRow keys={["Double-click"]} description="Add note (on canvas) / View image" />
              <ShortcutRow keys={["←", "→"]} description="Navigate lightbox" />
              <ShortcutRow keys={["Esc"]} description="Close lightbox / modal" />
              <ShortcutRow keys={["?"]} description="Show this help" />
              <ShortcutRow keys={["Scroll"]} description="Zoom in/out" />
              <ShortcutRow keys={["Drag"]} description="Pan canvas" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for shortcut rows
function ShortcutRow({ keys, description }: { keys: string[]; description: string }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1">
        {keys.map((key, i) => (
          <span key={i} className="flex items-center">
            <kbd className="px-2 py-1 bg-surface-elevated border border-border rounded text-xs text-foreground font-mono">
              {key}
            </kbd>
            {i < keys.length - 1 && <span className="text-muted mx-1">+</span>}
          </span>
        ))}
      </div>
      <span className="text-sm text-muted">{description}</span>
    </div>
  );
}
